package org.example.zone360.pages.coachDetails.sessionAllDetails.uploadAudio

import org.example.zone360.models.GetSessionsData
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import org.example.zone360.components.debugPrintln
import org.example.zone360.utils.APIEndPoints
import org.example.zone360.utils.Strings
import org.example.zone360.utils.getCurrentMilliseconds
import org.w3c.dom.url.URL
import org.w3c.files.File
import org.w3c.xhr.FormData
import org.w3c.xhr.XMLHttpRequest
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.math.ceil
import kotlin.math.min

suspend fun uploadAudioChunks(
    audioFile: File,
    userId: String,
    seasonID: String,
    organization: String,
    coachName: String,
    sessionName: String,
    sessionType: String = "",

    onProgress: (Double, String) -> Unit,
    onComplete: (Boolean, String?) -> Unit,
) {
    val originalExtension = audioFile.name.substringAfterLast(".", "aac")

    // Generate timestamp once and reuse
    val timestamp = getCurrentMilliseconds()
    val sessionId = "${sessionName}_${timestamp}.aac"
    val fileName = "${sessionName}_${timestamp}.$originalExtension"

    val durationSeconds = getAudioDuration(audioFile)
    val recordTime = durationSeconds.toLong().toString()


    val chunkSize = 1024 * 1024 // 1MB chunks
    val totalSize = audioFile.size.toDouble()
    val totalChunks = ceil(totalSize / chunkSize).toInt()

    onProgress(0.0, "Uploading in $totalChunks chunks...")

    try {
        for (chunkIndex in 0 until totalChunks) {
            val start = chunkIndex * chunkSize.toDouble()
            val end = minOf(start + chunkSize, totalSize)
            val chunkBlob = audioFile.slice(start.toInt(), end.toInt())

            val formData = FormData()

            // Add all the session data - convert everything to String
            formData.append("userID", userId)
            formData.append("recordTime", "0")
            formData.append("sessionID", sessionId)
            formData.append("filename", fileName)
            formData.append("coachName", coachName)
            formData.append("playerName", "")
            formData.append("statusUrl", "")
            formData.append("playerEmail", "")
            formData.append("playerID", "")
            formData.append("audioPath", "")
            formData.append("organization", organization)
            formData.append("playerOrganization", "")
            formData.append("sessionType", sessionType)
            formData.append("domainType", "soccer")
            formData.append("notificationID", "")
            formData.append("language", "en")
            formData.append("status", "cloud")
            formData.append("seasonID", seasonID)
            formData.append("text", "")
            formData.append("rating", "0")
            formData.append("category", "customAudio")

            // Chunk-specific data
            formData.append("chunkNumber", chunkIndex.toString())
            formData.append("totalChunks", totalChunks.toString())
            formData.append("file", chunkBlob, fileName)

            val xhr = XMLHttpRequest()
            xhr.open("POST", APIEndPoints.Name.upload_audio_chunks_new, true)

            // Track progress for this chunk
            val chunkDeferred = CompletableDeferred<Unit>()

            xhr.upload.onprogress = { event ->
                if (event.lengthComputable) {
                    val chunkProgress = (chunkIndex.toDouble() / totalChunks) +
                            (event.loaded.toDouble() / event.total.toDouble()) * (1.0 / totalChunks)
                    val overallProgress = chunkProgress * 100
                    onProgress(
                        overallProgress,
                        "Uploading chunk ${chunkIndex + 1}/$totalChunks"
                    )
                }
            }

            xhr.onload = { _ ->
                if (xhr.status.toInt() == 200) {
                    chunkDeferred.complete(Unit)
                } else {
                    chunkDeferred.completeExceptionally(Exception("Upload failed with status ${xhr.status}"))
                }
            }

            xhr.onerror = { _ ->
                chunkDeferred.completeExceptionally(Exception("Network error during chunk upload"))
            }

            xhr.send(formData)
            chunkDeferred.await()
            console.log("Chunk ${chunkIndex + 1}/$totalChunks uploaded")
        }

        // All chunks uploaded successfully
        onProgress(100.0, "Upload complete! Processing...")
        onComplete(true, "Upload successful")

    } catch (ce: CancellationException) {
        throw ce
    } catch (e: Throwable) {
        onComplete(false, e.message)
    }
}

suspend fun getAudioDuration(file: File): Double = suspendCancellableCoroutine { cont ->
    val url = URL.createObjectURL(file)
    val audio = js("new Audio()") as org.w3c.dom.HTMLAudioElement
    audio.src = url
    audio.onloadedmetadata = {
        val duration = audio.duration
        URL.revokeObjectURL(url)
        cont.resume(duration)
    }
    audio.onerror = { _, _, _, _, _ ->
        URL.revokeObjectURL(url)
        cont.resume(0.0)
    }
}